import {
  addExtensionListener,
  dispatchExtensionEvent,
  removeExtensionListener,
  requestWorkspaceLayoutEvent,
} from 'domains/extension/extensionEventCreators';
import { RESPONSE_EVENTS } from 'domains/extension/constants';
import { logger } from 'modules/logger';
import { type WorkspaceLayoutSnapshot } from './types';

function parseWorkspaceLayoutSnapshot(detail: unknown): WorkspaceLayoutSnapshot | null {
  if (detail == null || typeof detail !== 'object') {
    return null;
  }

  const workspaceLayout = (detail as { workspaceLayout?: unknown }).workspaceLayout;
  if (workspaceLayout == null || typeof workspaceLayout !== 'object') {
    return null;
  }

  const payload = workspaceLayout as WorkspaceLayoutSnapshot;

  if (!Array.isArray(payload.displays)) {
    return null;
  }

  if (!Array.isArray(payload.windows)) {
    return null;
  }

  return payload;
}

const WORKSPACE_LAYOUT_RESPONSE_TIMEOUT_MS = 5000;

export function getWorkspaceLayoutSnapshot(): Promise<WorkspaceLayoutSnapshot> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error('Timed out waiting for workspace layout snapshot'));
    }, WORKSPACE_LAYOUT_RESPONSE_TIMEOUT_MS);

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      removeExtensionListener(RESPONSE_EVENTS.WORKSPACE_LAYOUT_RESPONSE, handler);
    };

    const handler = (event: Event) => {
      const { detail } = event as CustomEvent<unknown>;
      const snapshot = parseWorkspaceLayoutSnapshot(detail);

      if (snapshot == null) {
        logger.warn('[WorkspaceLayoutManager] Received malformed workspace snapshot');

        cleanup();
        reject(new Error('Received malformed workspace snapshot'));

        return;
      }

      // Counts only: tab URLs can contain case IDs
      logger.info('[WorkspaceLayoutManager] Received workspace layout snapshot', {
        displayCount: snapshot.displays.length,
        windowCount: snapshot.windows.length,
      });

      cleanup();
      resolve(snapshot);
    };

    addExtensionListener(RESPONSE_EVENTS.WORKSPACE_LAYOUT_RESPONSE, handler);

    dispatchExtensionEvent(requestWorkspaceLayoutEvent());
  });
}
